import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import BulletinLikeHandler from '../BulletinLikeHandler';
import BulletinImage from '../BulletinImage';
import { formatDistanceToNow } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface RelicStageThreeProps {
  bulletin: any;
  onClose: () => void;
  onBack: () => void;
}

const RelicStageThree: React.FC<RelicStageThreeProps> = ({
  bulletin,
  onClose,
  onBack,
}) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = user?.id && bulletin?.user_id === user.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState<string>(bulletin?.title || '');
  const [editContent, setEditContent] = useState<string>(bulletin?.content || '');
  const [saving, setSaving] = useState(false);

  // Relic number UI removed; posts are permanent
  return (
    <BulletinLikeHandler
      bulletinId={bulletin.id}
      initialLikeCount={bulletin.like_count || 0}
      userId={user?.id}
      loopId={bulletin.loop_id}
      currentImageUrl={bulletin.image_url}
      currentThumbnailUrl={bulletin.thumbnail_url}
    >
      {(hasLiked, currentLikeCount, handleDoubleClick) => (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full h-full flex bg-gray-900"
          style={{
            maxWidth: isMobile ? '90vw' : '800px',
            maxHeight: isMobile ? '90vh' : '600px'
          }}
        >
          {/* Header */}
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Back button repositioned to left panel */}
          <div className="absolute top-4 left-4 z-10 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </div>

          {/* Left side - Information panel */}
          <div className="w-1/2 p-6 flex flex-col justify-between relative">
            {/* Desktop back button inside the panel */}
            <div className="hidden md:block absolute top-0 left-0 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-white/70 hover:text-white hover:bg-white/10 -ml-2"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                <span>Back</span>
              </Button>
            </div>
            {/* Title and content */}
            <div className="space-y-4 mt-8 md:mt-6">
              {!isEditing ? (
                <>
                  <h1 className="text-white text-2xl font-bold leading-tight break-words">
                    {bulletin?.title?.trim() || 'Untitled'}
                  </h1>
                  <div className="text-white/80 text-sm leading-relaxed break-words">
                    {bulletin?.content && bulletin.content.trim() ? (
                      <p>{bulletin.content.trim()}</p>
                    ) : (
                      <p className="italic text-white/60">No description provided.</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Title"
                    className="text-white bg-black/20 border-white/20 focus:border-white/40 focus:ring-white/20"
                    autoFocus
                  />
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={4}
                    placeholder="Description"
                    className="text-white bg-black/20 border-white/20 focus:border-white/40 focus:ring-white/20"
                  />
                </div>
              )}
            </div>

            {/* Bottom section - likes, tags, relic info */}
            <div className="space-y-4">
              {/* Like count */}
              <div className="flex items-center gap-2">
                <Heart className={`h-5 w-5 ${hasLiked ? 'fill-current text-pink-400' : 'text-white/80'}`} />
                <span className="text-white font-semibold">{currentLikeCount}</span>
              </div>

              {/* Tags with tooltip */}
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="flex gap-2 flex-wrap cursor-help">
                      {bulletin.content_labels && bulletin.content_labels.length > 0 ? (
                        bulletin.content_labels.slice(0, 5).map((label, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary" 
                            className="bg-white/10 text-white border-white/20"
                          >
                            {label.toLowerCase()}
                          </Badge>
                        ))
                      ) : (
                        <>
                          <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                            artwork
                          </Badge>
                          <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                            digital
                          </Badge>
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-xs leading-snug">
                    We believe transparency is important, these tags were generated for you so that others can find your content easier!
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Owner controls */}
              {isOwner && (
                <div className="flex gap-2 pt-1">
                  {!isEditing ? (
                    <Button size="sm" variant="secondary" className="bg-white/10 text-white border-white/20" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white/10 text-white border-white/20"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            
                            // First try updating both title and content
                            let updateResult = await supabase
                              .from('bulletins')
                              .update({ title: editTitle?.trim() || '', content: editContent || '' })
                              .eq('id', bulletin.id)
                              .eq('user_id', user!.id);
                              
                            // If error mentions title column doesn't exist, update only content
                            if (updateResult.error && updateResult.error.message?.includes('title')) {
                              console.log('Title column not found, updating content only');
                              updateResult = await supabase
                                .from('bulletins')
                                .update({ content: editContent || '' })
                                .eq('id', bulletin.id)
                                .eq('user_id', user!.id);
                            }
                            
                            if (updateResult.error) throw updateResult.error;
                            
                            // Invalidate queries to refetch the bulletin data
                            queryClient.invalidateQueries({ queryKey: ['bulletins'] });
                            queryClient.invalidateQueries({ queryKey: ['bulletin', bulletin.id] });
                            
                            toast.success('Post updated');
                            setIsEditing(false);
                          } catch (err: any) {
                            console.error(err);
                            toast.error('Failed to update post');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditTitle(bulletin?.title || ''); setEditContent(bulletin?.content || ''); }}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Relic number removed - posts are permanent */}
            </div>
          </div>

          {/* Right side - Image */}
          <div className="w-1/2 flex items-center justify-center">
            <div className="w-full h-full overflow-hidden">
              <BulletinImage
                src={bulletin.image_url}
                fileType={bulletin.file_type}
                isSelected={true}
                onClick={() => {}}
                onLoad={() => {}}
                onError={() => {}}
                size="large"
                isMature={bulletin.is_mature}
                fillContainer={true}
              />
            </div>
          </div>
        </motion.div>
      )}
    </BulletinLikeHandler>
  );
};

export default RelicStageThree;